import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Lock, Mail, Building2, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import PageMeta from "../../components/seo/PageMeta";
import { SHIFTS_CTAS } from "../../data/marketplaceData";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShiftsLogin() {
  const navigate = useNavigate();
  const { user, setSession } = useAuth();
  const [params] = useSearchParams();
  const intent = params.get("intent");
  const shiftId = params.get("shift");

  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  /* If already signed in — go straight where they wanted to go */
  useEffect(() => {
    if (user) {
      if (intent === "claim" && shiftId) navigate(`${SHIFTS_CTAS.browse}?shift=${shiftId}`);
      else navigate("/dashboard");
    }
  }, [user, intent, shiftId, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Invalid credentials");
      if (setSession) setSession(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name?.split(" ")[0] || "clinician"}.`);
      if (intent === "claim" && shiftId) navigate(`${SHIFTS_CTAS.browse}?shift=${shiftId}`);
      else navigate("/dashboard");
    } catch (err) {
      toast.error("Sign in failed", { description: err?.message || "Check your email and password." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ShiftsLayout>
      <PageMeta
        title="Sign in — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Sign in to TRITAL ShiftBridge™ to claim PRN shifts, post shifts, or manage your facility account."
        canonical="https://www.tritalcare.com/shiftbridge/login"
        noIndex
      />
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-14 lg:py-24">
        <Link to="/shifts" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-500 hover:text-trital-navy mb-8" data-testid="login-back">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> Back to TRITAL ShiftBridge™
        </Link>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
          {/* LOGIN CARD */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-3xl bg-white border border-slate-200 shadow-navy p-8 lg:p-10"
            data-testid="login-card"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-trital-medical-blue-soft text-trital-navy">
              <Lock className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span className="overline">Sign in</span>
            </div>
            <h1 className="mt-4 font-display font-black text-3xl lg:text-4xl text-slate-900 text-balance">Welcome back to TRITAL ShiftBridge™.</h1>
            {intent === "claim" && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-trital-red/10 border border-trital-red/20 text-trital-red text-[12px] font-semibold">
                Sign in to claim this shift
              </div>
            )}

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
              <label className="block">
                <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">Email</span>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.8} />
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="login-email" className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-trital-navy focus:ring-2 focus:ring-trital-navy/10" />
                </div>
              </label>
              <label className="block">
                <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">Password</span>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.8} />
                  <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="login-password" className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-trital-navy focus:ring-2 focus:ring-trital-navy/10" />
                </div>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {submitting ? "Signing in…" : <>Sign in <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>}
              </button>

              <div className="flex items-center justify-between text-[12px] text-slate-500 pt-2">
                <Link to="/shifts/contact" className="hover:text-trital-navy">Forgot password?</Link>
                <span>New here? <Link to={SHIFTS_CTAS.clinicianSignup} className="font-semibold text-trital-navy hover:underline">Create account</Link></span>
              </div>
            </form>
          </motion.div>

          {/* SIDE — choose a signup path */}
          <aside className="space-y-3">
            <div className="rounded-3xl bg-trital-mesh border border-slate-200 p-7">
              <div className="overline text-trital-red">No account yet?</div>
              <h2 className="mt-3 font-display font-black text-2xl text-slate-900">Pick the path that fits you.</h2>
              <p className="mt-2 text-[13.5px] text-slate-600">Sign up free as a clinician, or post your first shift as a facility.</p>

              <div className="mt-6 space-y-3">
                <Link to={SHIFTS_CTAS.clinicianSignup} className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 hover:border-trital-red transition-colors" data-testid="login-cta-clinician">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-trital-red text-white flex items-center justify-center"><HeartPulse className="w-5 h-5" strokeWidth={1.8} /></div>
                    <div>
                      <div className="font-display font-bold text-slate-900">I'm a clinician</div>
                      <div className="text-[12.5px] text-slate-600 mt-0.5">Free signup. Claim shifts in 72 hours.</div>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-trital-red group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                </Link>
                <Link to={SHIFTS_CTAS.facilitySignup} className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 hover:border-trital-navy transition-colors" data-testid="login-cta-facility">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-trital-navy text-white flex items-center justify-center"><Building2 className="w-5 h-5" strokeWidth={1.8} /></div>
                    <div>
                      <div className="font-display font-bold text-slate-900">I'm a facility</div>
                      <div className="text-[12.5px] text-slate-600 mt-0.5">Post your first shift in 90 seconds.</div>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-trital-navy group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                </Link>
              </div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-[13px] text-slate-600">
              Account locked or security concern? Email <a href="mailto:support@tritalcare.com" className="font-semibold text-trital-navy hover:underline">support@tritalcare.com</a> — replied within 1 business hour.
            </div>
          </aside>
        </div>
      </section>
    </ShiftsLayout>
  );
}
